import { useQuery } from '@apollo/client'
import { FunctionComponent, useState } from 'react'
import {
  Button,
  ButtonStyle,
  Icon,
  IconType,
  ListOptions,
} from '../../../components'
import { GET_TEAM_INVITATIONS } from '../../../gql'
import {
  Brand,
  GetTeamInvitationsSort,
  User,
  UserRole,
} from '../../../gql/types'
import { LIMIT_DEFAULT, SKIP_DEFAULT } from '../../../utils/constants'
import { useUserBrand } from '../../../hooks'
import { Header } from '../components/Header'
import { InviteMemberModal } from './components/InviteMemberModal'
import { List } from './components/List'
import { useParams } from 'react-router-dom'

interface IDefaultProps {}

interface IProps extends Partial<IDefaultProps> {
  user: User
  currentBrand: Brand
}

const defaultProps: IDefaultProps = {}

const BrandInvitations: FunctionComponent<IProps> = ({
  user,
  currentBrand,
}) => {
  const userBrand = useUserBrand(currentBrand?.id)

  const [isInviteModalOpen, setIsInviteModalOpen] = useState(false)

  const [options, setOptions] = useState({
    query: null,
    filters: {},
    sort: GetTeamInvitationsSort.newest,
    skip: SKIP_DEFAULT,
    limit: LIMIT_DEFAULT,
  })

  const { loading, data } = useQuery(GET_TEAM_INVITATIONS, {
    variables: {
      options: {
        ...options,
        filters: {
          ...options.filters,
          brandId: currentBrand?.id,
        },
      },
    },
  })

  const [query, setQuery] = useState('')
  const onSearch = () => {
    setOptions({
      ...options,
      query,
      filters: {
        ...options.filters,
      },
    })
  }

  const setPage = (page) => {
    setOptions({
      ...options,
      skip: page * options.limit,
    })
  }

  return (
    <div>
      <Header brand={currentBrand} />

      <div className='sm:flex sm:items-center px-2'>
        <div className='sm:flex-auto'>
          <h2 className='text-lg font-medium leading-6 text-gray-900'>
            Invitations
          </h2>
          <p className='mt-1 text-sm text-gray-500'>
            Manage your brand's team invitations.
          </p>
        </div>
        {userBrand?.roles?.includes(UserRole.owner) && (
          <div className='mt-4 sm:ml-16 sm:mt-0 sm:flex-none'>
            <Button
              styleType={ButtonStyle.fill}
              title='Invite'
              type='button'
              icon={
                <Icon
                  type={IconType.personAdd}
                  className='h-5 w-5 text-white'
                  aria-hidden='true'
                />
              }
              onClick={() => setIsInviteModalOpen(!isInviteModalOpen)}
              iconPosition='left'
            />
          </div>
        )}
      </div>

      <div className='my-4 w-auto h-fit'>
        <ListOptions
          query={query}
          setQuery={setQuery}
          onSearch={onSearch}
          isSearchBarFullWidth
        />

        <div className='overflow-x-auto'>
          <div className='inline-block min-w-full py-2 px-1 align-middle'>
            <div className='overflow-hidden shadow  md:w-auto ring-1 ring-black ring-opacity-5 rounded-t'>
              <List
                brand={currentBrand}
                teamMembersInvitations={
                  data?.getTeamMembersInvitations?.items ?? []
                }
                pagination={data?.getTeamMembersInvitations?.pagination}
                setPage={setPage}
                loading={loading}
              />
            </div>
          </div>
        </div>
      </div>

      <InviteMemberModal
        brand={currentBrand}
        isOpen={isInviteModalOpen}
        setIsOpen={setIsInviteModalOpen}
      />
    </div>
  )
}

BrandInvitations.defaultProps = defaultProps

export { BrandInvitations }
